import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium cursor-pointer transition-all duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-[var(--color-sidebar-border)] bg-[var(--color-sidebar)] text-foreground shadow-sm hover:bg-white/10 hover:text-white",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // UI Styling Guide variants - using design tokens (see docs/ui-styling.md)
        blue: "bg-[var(--color-interactive-blue-bg)] border border-[var(--color-interactive-blue-border)] text-[var(--color-interactive-blue-text)] hover:bg-[var(--color-interactive-blue-bghover)] hover:border-[var(--color-interactive-blue-borderhover)] hover:text-[var(--color-interactive-blue-texthover)] active:bg-[var(--color-interactive-blue-bgactive)]",
        cobalt:
          "bg-[var(--color-interactive-cobalt-bg)] border border-[var(--color-interactive-cobalt-bg)] text-white hover:bg-[var(--color-interactive-cobalt-bghover)] hover:border-[var(--color-interactive-cobalt-bghover)] active:bg-[var(--color-interactive-cobalt-bgactive)]",
        pink: "bg-[var(--color-interactive-pink-bg)] border border-[var(--color-interactive-pink-bg)] text-white hover:bg-[var(--color-interactive-pink-bghover)] hover:border-[var(--color-interactive-pink-bghover)] active:bg-[var(--color-interactive-pink-bgactive)]",
        grey: "bg-[var(--color-interactive-grey-bg)] border border-[var(--color-interactive-grey-border)] text-[var(--color-interactive-grey-text)] hover:bg-[var(--color-interactive-grey-bghover)] hover:border-[var(--color-interactive-grey-borderhover)] hover:text-[var(--color-interactive-grey-texthover)] active:bg-[var(--color-interactive-grey-bgactive)] dark:bg-[var(--color-interactive-grey-bgdark)] dark:border-[var(--color-interactive-grey-borderdark)] dark:text-[var(--color-interactive-grey-textdark)] dark:hover:bg-[var(--color-interactive-grey-bgdarkhover)] dark:hover:border-[var(--color-interactive-grey-borderdarkhover)] dark:hover:text-[var(--color-interactive-grey-textdarkhover)] dark:active:bg-[var(--color-interactive-grey-bgdarkactive)]",
        red: "bg-[var(--color-interactive-red-bg)] border border-[var(--color-interactive-red-border)] text-[var(--color-interactive-red-text)] hover:bg-[var(--color-interactive-red-bghover)] hover:border-[var(--color-interactive-red-borderhover)] hover:text-[var(--color-interactive-red-texthover)] active:bg-[var(--color-interactive-red-bgactive)]",
        // Low-emphasis utility buttons (close, menu, etc.) - see frontend-patterns.md
        utility:
          "bg-transparent border-none text-[var(--color-interactive-utility-text)] hover:bg-[var(--color-interactive-utility-bghover)] hover:text-[var(--color-interactive-utility-texthover)] active:scale-95",
        // Low-emphasis delete buttons - see frontend-patterns.md
        "utility-destructive":
          "bg-transparent border-none text-[var(--color-interactive-utility-text)] hover:bg-[var(--color-interactive-utilitydestructive-bghover)] hover:text-[var(--color-interactive-utilitydestructive-texthover)] active:scale-95",
      },
      size: {
        default: "h-9 px-4 py-2 rounded",
        sm: "h-8 px-3.5 text-[13px] rounded",
        lg: "h-9 px-4 text-sm rounded",
        icon: "h-8 w-8 rounded",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          "select-none",
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
